import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ColumnState } from 'ag-grid-community';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { PRD3040_R01TabType } from './Store_PRD3040_R01';
import { getTabGridOptions } from './components/ColumnDefinitions';
import usePRD3040_R01Store from './Store_PRD3040_R01';
import { aggridHeaderClass } from 'src/components/common/common';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { 
    isSubtotalRow, 
    isDateSubtotalRow, 
    isItemSubtotalRow, 
    isWorkplaceSubtotalRow,
    mergeDataWithSubtotals,
    getSubtotalCalculator,
	GridRowData,
	defaultGridRowData
} from './Dto_PRD3040_R01';
import { formatNumber } from 'src/utils/number/numberUtils';
import { getDetailRowId, getSubtotalRowId, PRD3040_R01_TABS } from './utils/tabConfig';

const BOTTOM_LABEL_COLS = new Set(['itemCd', 'workplaceNm', 'compNm']);

const normalizeHeaderClass = (defs: ColDef[]): ColDef[] =>
	defs.map((def: ColDef & { children?: ColDef[] }) => {
		const mergedHeaderClass = [aggridHeaderClass, def.headerClass].filter(Boolean).join(' ');
		const children = Array.isArray(def.children) && def.children.length > 0
			? normalizeHeaderClass(def.children)
			: undefined;
		return { ...def, headerClass: mergedHeaderClass, suppressMenu: true, children };
	});

const applyPinnedCenterAlign = (defs: ColDef[]): ColDef[] =>
	defs.map((def: ColDef & { colId?: string; cellStyle?: ColDef['cellStyle'] }) => {
		if (!def?.colId || !BOTTOM_LABEL_COLS.has(def.colId)) return def;
		const prevCellStyle = def.cellStyle;
		const cellStyle = (params: { node?: { rowPinned?: string | null } }) => {
			if (params?.node?.rowPinned === 'bottom') {
				return {
					...(typeof prevCellStyle === 'function' ? prevCellStyle(params as never) : prevCellStyle),
					textAlign: 'center',
				};
			}
			return typeof prevCellStyle === 'function' ? prevCellStyle(params as never) : prevCellStyle;
		};
		return { ...def, cellStyle };
	});

// 날짜를 YYYY-MM-DD 형식으로 포맷팅하는 함수
const formatDateToYYYYMMDD = (dateString: string): string => {
    if (!dateString) return '';
    
    try {
        const dateOnly = dateString.split('T')[0]; // T 이전 부분만 가져오기
        return dateOnly; // 이미 YYYY-MM-DD 형식
    } catch (error) {
        console.warn('날짜 포맷팅 오류:', error);
        return dateString;
    }
};

interface StockGridBaseProps {
    gridRef: React.RefObject<AgGridReact<any> | null>;
    tabType: PRD3040_R01TabType;
    data: any[];
    columns: ColDef[];
    onTabChange: (tab: PRD3040_R01TabType) => Promise<void>;
}

export const Table_PRD3040_R01: React.FC<StockGridBaseProps> = ({
    gridRef,
    tabType,
    data,
    columns,
    onTabChange
}) => {
    const {
        PRD3040_R01activeTab,
        PRD3040_R01tabColumnStates,
        setPRD3040_R01TabColumnState,
        PRD3040_R01tabSelectedIds,
        setPRD3040_R01TabSelectedIds,
        PRD3040_R01showSubtotals,
        PRD3040_R01tabSubtotalData,
        setPRD3040_R01TabSubtotalData,
        PRD3040_R01tabGridData,
        setPRD3040_R01TabGridData,
        PRD3040_R01tabScrollTop,
        setPRD3040_R01TabScrollTop,
    } = usePRD3040_R01Store();

    // AG Grid 충돌 방지를 위한 키
    const [gridKey, setGridKey] = useState(0);
    const [, setHoveredRowIndex] = useState<number | null>(null);
    
    // 탭 변경 시 AG Grid를 완전히 새로 생성
    useEffect(() => {
        setGridKey(prev => prev + 1);
    }, [tabType]);

    // 소계 계산 및 그리드 데이터 생성
    useEffect(() => {
        if (data && data.length > 0) {
            // 소계 계산
            const calculateSubtotals = getSubtotalCalculator(tabType);
            const subtotals = calculateSubtotals(data);
            setPRD3040_R01TabSubtotalData(tabType, subtotals);
            
            // 소계와 데이터 병합
            const mergedData = mergeDataWithSubtotals(data, subtotals, PRD3040_R01showSubtotals, tabType);
            setPRD3040_R01TabGridData(tabType, mergedData);
        } else {
            setPRD3040_R01TabSubtotalData(tabType, []);
            setPRD3040_R01TabGridData(tabType, []);
        }
    }, [data, tabType, PRD3040_R01showSubtotals, setPRD3040_R01TabSubtotalData, setPRD3040_R01TabGridData]);

    // 소계 표시 상태 변경시 그리드 데이터 재생성
    useEffect(() => {
        if (data && data.length > 0 && PRD3040_R01tabSubtotalData[tabType].length > 0) {
            const mergedData = mergeDataWithSubtotals(data, PRD3040_R01tabSubtotalData[tabType], PRD3040_R01showSubtotals, tabType);
            setPRD3040_R01TabGridData(tabType, mergedData);
        }
    }, [PRD3040_R01showSubtotals, data, tabType, PRD3040_R01tabSubtotalData, setPRD3040_R01TabGridData]);

    // 현재 탭의 그리드 데이터 사용
    const currentGridData = PRD3040_R01tabGridData[tabType] || [];
    
    // 로컬 스토리지 키
    const localStorageKey = useMemo(() => `PRD3040_R01_${tabType}_columns`, [tabType]);
    const tabs = PRD3040_R01_TABS;

    // 탭 변경 핸들러 (UI 이벤트만 처리)
    const handleTabClick = useCallback(async (tab: PRD3040_R01TabType) => {
        if (tab === PRD3040_R01activeTab) return; // 같은 탭 클릭 시 무시
        
        // 부모 컴포넌트의 onTabChange 호출
        await onTabChange(tab);
    }, [onTabChange, PRD3040_R01activeTab]);

    // 행 ID 생성 함수 - 생산실적 규격
    const getRowId = useCallback((params: any) => {
        const data = params.data;

        // 소계 행은 그룹키 기반의 별도 ID를 사용해 상세 행과 충돌을 방지한다.
        if (isSubtotalRow(data)) {
            return getSubtotalRowId(tabType, data.groupKey);
        }

        return getDetailRowId(tabType, data) || params.node.id;
    }, [tabType]);

    // 데이터 객체만 받아 동일 규칙으로 ID 생성 - 생산실적 규격
    const makeRowId = useCallback((data: any) => {
        if (isSubtotalRow(data)) {
            return getSubtotalRowId(tabType, data.groupKey);
        }

        return getDetailRowId(tabType, data);
    }, [tabType]);


    // 소계 행 스타일 함수
    const getCellStyle = useCallback((params: any, key: string) => {
        if (isSubtotalRow(params.data)) {
            const isNumericColumn = ['orderQty', 'productQty', 'badQty', 'acceptQty'].includes(key);
            
            if (isDateSubtotalRow(params.data)) {
                return {
                    backgroundColor: '#f0f8ff',
                    color: '#2c5aa0',
                    fontWeight: 'bold',
                    borderTop: '2px solid #2c5aa0',
                    borderBottom: '1px solid #2c5aa0',
                    textAlign: isNumericColumn ? 'right' : 'left',
                };
            } else if (isItemSubtotalRow(params.data)) {
                return {
                    backgroundColor: '#fff8f0',
                    color: '#a05a2c',
                    fontWeight: 'bold',
                    borderTop: '2px solid #a05a2c',
                    borderBottom: '1px solid #a05a2c',
                    textAlign: isNumericColumn ? 'right' : 'left',
                };
            } else if (isWorkplaceSubtotalRow(params.data)) {
                return {
                    backgroundColor: '#f0fff0',
                    color: '#2c7a2c',
                    fontWeight: 'bold',
                    borderTop: '2px solid #2c7a2c',
                    borderBottom: '1px solid #2c7a2c',
                    textAlign: isNumericColumn ? 'right' : 'left',
                };
            }
        }
        return undefined;
    }, []);

    const baseColumnDefs: ColDef<GridRowData>[] = useMemo(() => [
        ...getDefaultRowIndexColumnConfig(),
        ...columns.map(col => ({
            ...col,
            cellStyle: (params: any) => getCellStyle(params, col.colId || ''),
            cellRenderer: (params: any) => {
                if (isSubtotalRow(params.data)) {
                    if (col.colId === 'productDt' && isDateSubtotalRow(params.data)) {
                        return `${params.data.groupLabel} (${params.data.count}건)`;
                    } else if (col.colId === 'itemCd' && isItemSubtotalRow(params.data)) {
                        return `${params.data.groupLabel} (${params.data.count}건)`;
                    } else if (col.colId === 'workplaceNm' && isWorkplaceSubtotalRow(params.data)) {
                        return `${params.data.groupLabel} (${params.data.count}건)`;
                    } else if (['orderQty', 'productQty', 'badQty', 'acceptQty'].includes(col.colId || '')) {
                        return formatNumber(params.data[col.colId as keyof typeof params.data] || 0);
                    } else if (col.colId === 'itemNm' && isItemSubtotalRow(params.data)) {
                        return params.data.itemNm || '';
                    } else if (col.colId === 'itemSpec' && isItemSubtotalRow(params.data)) {
                        return params.data.itemSpec || '';
                    } else if (col.colId === 'itemUnit' && isItemSubtotalRow(params.data)) {
                        return params.data.itemUnit || '';
                    }
                    return '';
                }
                if (col.colId === 'productDt') {
                    return formatDateToYYYYMMDD(params.value || '');
                }
                if (col.valueFormatter && typeof col.valueFormatter === 'function') {
                    return col.valueFormatter(params);
                }
                return params.value ?? '';
            },
        })),
    ] as ColDef<GridRowData>[], [columns, getCellStyle]);

    const effectiveColumnState = useMemo(() => {
        const savedState = PRD3040_R01tabColumnStates[tabType];
        return Array.isArray(savedState) && savedState.length === 0 ? null : savedState;
    }, [PRD3040_R01tabColumnStates, tabType]);

    const defaultColumnState = useMemo(
        () => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
    , [baseColumnDefs]);

    const columnDefs = useMemo(() => {
        const mergedColumnDefs = effectiveColumnState === null
            ? baseColumnDefs
            : GridCustomColumnDefs({
                baseColumnDefs,
                columnState: effectiveColumnState,
                headerNames: {},
                fallbackHeaderClass: aggridHeaderClass,
            });
        return applyPinnedCenterAlign(normalizeHeaderClass(mergedColumnDefs));
    }, [baseColumnDefs, effectiveColumnState]);

    const handleSetColumnState = useCallback((state: ColumnState[] | null) => {
        setPRD3040_R01TabColumnState(tabType, state || []);
    }, [setPRD3040_R01TabColumnState, tabType]);

    // 이 테이블 전용 기본 컬럼 옵션(헤더 아이콘 숨김)
    const localDefaultColDef = useMemo(() => ({
        ...defaultColDef,
        suppressMenu: true,
        headerClass: aggridHeaderClass,
    }), []);
    
    // 행 선택 하이라이트 규칙
    const rowClassRules = useMemo(() => {
        const selectedList = PRD3040_R01tabSelectedIds[tabType] || [];
        return {
            selected: (params: any) => {
                if (params.node.rowPinned === 'bottom') return false;
                if (isSubtotalRow(params.data)) return false;
                const id = makeRowId(params.data);
                return selectedList.includes(id);
            },
            hover: (params: any) => {
                if (params.node.rowPinned === 'bottom') return false;
                if (!params.data) return false;
                if (isSubtotalRow(params.data)) return false;
                const id = makeRowId(params.data);
                return !selectedList.includes(id);
            },
        } as Record<string, (p: any) => boolean>;
    }, [PRD3040_R01tabSelectedIds, tabType, makeRowId]);

    const shiftRangeSelectionConfig = useMemo(() => ({
        enableShiftRangeSelect: true,
        selectedIds: PRD3040_R01tabSelectedIds[tabType] || [],
        setSelectedIds: (ids: Array<string | number>) => setPRD3040_R01TabSelectedIds(tabType, ids.map(String)),
        resolveId: (row: GridRowData) => makeRowId(row),
        isRowSelectable: (event: any) => !isSubtotalRow(event.data),
    }), [PRD3040_R01tabSelectedIds, tabType, setPRD3040_R01TabSelectedIds, makeRowId]);

    // 합계(핀드 바텀 로우) 계산 - 생산실적 합계 (소계 행 제외)
    const pinnedBottomRowData = useMemo(() => {
        if (!Array.isArray(currentGridData) || currentGridData.length === 0) return [] as any[];
        
        // 소계 행 제외한 상세 데이터만 계산
        const detailRows = currentGridData.filter(row => !isSubtotalRow(row));
        const number = (v: any) => Number(v) || 0;
        const dataLength = detailRows?.length || 0;
        
        const total = {
            itemCd: `${dataLength.toLocaleString()}건`,
            orderQty: detailRows.reduce((s, r: any) => s + number(r.orderQty), 0),
            productQty: detailRows.reduce((s, r: any) => s + number(r.productQty), 0),
            badQty: detailRows.reduce((s, r: any) => s + number(r.badQty), 0),
            acceptQty: detailRows.reduce((s, r: any) => s + number(r.acceptQty), 0),
        } as any;
        return [total];
    }, [currentGridData, tabType]);

    return (
        <div className="w-full h-full flex flex-col">
            {/* 탭 버튼 섹션 - GridContextWrapper 위에 위치 */}
            <div
                className="flex items-end mb-[0.1px]  bg-white h-[25px] "
                role="tablist"
                aria-label="생산실적 조회 탭"
            >
                {tabs.map((tab) => {
                    const isActive = PRD3040_R01activeTab === (tab.id as PRD3040_R01TabType);
                    return (
                        <button
                            key={tab.id}
                            onClick={() => handleTabClick(tab.id as PRD3040_R01TabType)}
                            role="tab"
                            aria-selected={isActive}
                            className={`h-[26px] px-3 text-[12px] font-medium transition-colors border border-gray-300 border-b-0
                                ${isActive
                                    ? 'bg-white text-black-700 -mb-px shadow-sm'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
                            `}
                        >
                            {tab.label}
                        </button>
                    );
                })}
            </div>

            {/* 그리드 섹션 */}
            <div className="flex-1">
                <GridContextWrapper<GridRowData>
                	enableCellCalculation
                    gridRef={gridRef}
                    paddedGridData={currentGridData}
                    initSelected={() => {}}
					defaultRowData={defaultGridRowData}
                    baseColumnDefs={effectiveColumnState ? columnDefs : baseColumnDefs}
                    defaultColumnState={defaultColumnState}
                    setColumnState={handleSetColumnState}
                    localStorageKey={localStorageKey}
                    menuItems={undefined}
                >
                    <Grid_Touch<GridRowData>
                        key={gridKey}
                        ref={gridRef as any}
                        rowData={currentGridData}
                        columnDefs={columnDefs}
                        defaultColDef={localDefaultColDef}
                        getRowId={getRowId}
                        onCellKeyDown={undefined}
                        rowClassRules={rowClassRules}
                        pinnedBottomRowData={pinnedBottomRowData}
                        shiftRangeSelection={shiftRangeSelectionConfig}
                        setHoveredRowIndex={setHoveredRowIndex}
                        columnStateKey={localStorageKey}
                        setColumnState={handleSetColumnState}
                        isRowSelectable={(node: any) => !!node.data && !isSubtotalRow(node.data as GridRowData)}
                        scrollTop={PRD3040_R01tabScrollTop[tabType]}
                        setScrollTop={(top) => setPRD3040_R01TabScrollTop(tabType, top)}
                        {...getTabGridOptions()}
                    />
                </GridContextWrapper>
            </div>
        </div>
    );
};
